/**
 * CDC Adult Immunization Recommendations
 *
 * Vaccine schedules and representative CVX codes for adult immunizations
 * based on ACIP recommendations.
 *
 * @see https://www.cdc.gov/vaccines/hcp/imz-schedules/adult-age.html
 * @see https://www.cdc.gov/mmwr/volumes/74/wr/mm7402a3.htm
 *
 * Last updated: 2025-01 (based on 2025 ACIP recommendations)
 *
 * Note: Each vaccine family may have multiple CVX codes representing different
 * products/formulations. The codes listed here are representative examples,
 * typically the most commonly used active products.
 */

import {
  CVX_SYSTEM,
  getActiveCVXCodesForFamily,
  getCVXCodesForFamily,
  type VaccineFamily,
} from '#src/cdc/vaccines/cvx-mapping.ts'

export interface AdultVaccineInfo {
  /**
   * Vaccine family name (matches VaccineFamily type)
   */
  family: VaccineFamily

  /**
   * All CVX codes for this vaccine family (active and inactive)
   * Derived from CVX_TO_VACCINE_GROUP mapping
   */
  cvxCodes: string[]

  /**
   * Only active CVX codes for this vaccine family
   * Derived from CVX_TO_VACCINE_GROUP mapping
   */
  activeCvxCodes: string[]

  /**
   * Display name for the vaccine family
   */
  display: string

  /**
   * Coding system for CVX codes (always CVX)
   */
  system: typeof CVX_SYSTEM

  /**
   * Short description of the vaccine or recommendation
   */
  description?: string
}

/**
 * Helper to create AdultVaccineInfo with CVX codes derived from mapping
 */
function createAdultVaccineInfo(family: VaccineFamily): AdultVaccineInfo {
  return {
    family,
    cvxCodes: getCVXCodesForFamily(family),
    activeCvxCodes: getActiveCVXCodesForFamily(family),
    display: family,
    system: CVX_SYSTEM,
    description: family,
  }
}

/**
 * CDC Adult Immunization Schedule - Recommended Vaccines
 *
 * CVX codes are automatically derived from CVX_TO_VACCINE_GROUP mapping.
 * This ensures consistency and maintainability as CVX codes are updated.
 *
 * @see https://www.cdc.gov/vaccines/hcp/imz-schedules/adult-age.html
 * @see https://www.cdc.gov/mmwr/volumes/74/wr/mm7402a3.htm
 */
export const CDC_ADULT_VACCINES: readonly AdultVaccineInfo[] = [
  createAdultVaccineInfo('COVID-19'),
  createAdultVaccineInfo('Influenza'),
  createAdultVaccineInfo('HPV'),
  createAdultVaccineInfo('Hepatitis A'),
  createAdultVaccineInfo('Hepatitis B'),
  createAdultVaccineInfo('Hib'),
  createAdultVaccineInfo('MMR'),
  createAdultVaccineInfo('Meningococcal (ACWY)'),
  createAdultVaccineInfo('Meningococcal (B)'),
  createAdultVaccineInfo('Mpox'),
  createAdultVaccineInfo('Pneumococcal'),
  createAdultVaccineInfo('RSV'),
  createAdultVaccineInfo('Tetanus/Diphtheria (Td)'),
  createAdultVaccineInfo('Tdap'),
  createAdultVaccineInfo('Varicella'),
  createAdultVaccineInfo('Zoster (Shingles)'),
] as const

/**
 * Get vaccine info by family name
 */
export function getAdultVaccineInfo(
  family: VaccineFamily
): AdultVaccineInfo | undefined {
  return CDC_ADULT_VACCINES.find((v) => v.family === family)
}

/**
 * Get all vaccine families in the adult schedule
 */
export function getAdultVaccineFamilies(): VaccineFamily[] {
  return CDC_ADULT_VACCINES.map((v) => v.family)
}
